import { downloadFile } from "@/utils/downloadFile";
import {
  buildDiagnosticReportData,
  type BuildReportInput,
} from "./diagnosticReportData";

/**
 * The renderer can stall rather than reject on a bad document (an unregistered
 * font face is the usual cause), which would otherwise leave the caller's
 * "generating" state stuck forever. Fail loudly instead.
 *
 * The wrapper goes round the dynamic import as well as the render: the library
 * is a ~1 MB chunk fetched on demand, so a lost connection stalls there just as
 * silently.
 */
const PDF_TIMEOUT_MS = 30_000;

const withTimeout = <T,>(work: Promise<T>, label: string): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`${label} timed out after ${PDF_TIMEOUT_MS}ms`)),
      PDF_TIMEOUT_MS,
    );
    work.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });

/**
 * Punctuation out, spaces to hyphens. Capitals are deliberately preserved
 * (decision D14): this file is handed to a business owner, and
 * `Stage-Diagnostic-Acme-Heating-Lead-2026-08-19.pdf` reads better than an
 * all-lowercase name.
 */
const slugify = (value: string): string =>
  value
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

/**
 * The completion date as the reader's own calendar shows it.
 *
 * `toISOString()` is UTC, and the document body prints
 * `toLocaleDateString()` — so west of Greenwich a diagnostic completed in the
 * evening was filed under tomorrow's date while its own first page said
 * today's.
 */
const localCalendarDate = (value: string): string => {
  const date = new Date(value);
  const pad = (part: number): string => String(part).padStart(2, "0");
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
  ].join("-");
};

/** `Stage-Diagnostic-Acme-Heating-Lead-2026-08-19.pdf` */
export const diagnosticReportFileName = (
  input: Pick<
    BuildReportInput,
    "diagnosticName" | "subjectName" | "completedAt"
  >,
  stageLabel: string,
): string => {
  const datePart = input.completedAt
    ? localCalendarDate(input.completedAt)
    : "draft";

  return (
    [
      slugify(input.diagnosticName),
      slugify(input.subjectName),
      slugify(stageLabel),
      datePart,
    ]
      .filter(Boolean)
      .join("-") + ".pdf"
  );
};

/**
 * Render a diagnostic to a PDF and trigger a browser download.
 *
 * Mirrors the Scoreboard leaderboard certificates: a real generated document
 * rather than a print of the page. The ~1 MB @react-pdf/renderer library and
 * the template are dynamically imported together so they stay out of the
 * diagnostics chunk until a download is actually requested — the template is
 * the only module that imports the library statically, so nothing else pulls
 * it in.
 *
 * Callers must pass `recordedStageLabel`; see `BuildReportInput` for why.
 */
export const downloadDiagnosticReport = async (
  input: BuildReportInput,
): Promise<void> => {
  const [{ pdf }, { default: DiagnosticReportPdf }] = await withTimeout(
    Promise.all([
      import("@react-pdf/renderer"),
      import("./DiagnosticReportPdf"),
    ]),
    "Loading the PDF renderer",
  );

  const data = buildDiagnosticReportData(input);

  const blob = await withTimeout(
    pdf(<DiagnosticReportPdf data={data} />).toBlob(),
    "Rendering the PDF",
  );

  downloadFile(blob, diagnosticReportFileName(input, data.stageLabel));
};
